// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { NgxEchartsDirective, provideEcharts } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { Analysis } from "../../../../interfaces/analysis";
import { RefsNameMapping, SvChrOpts, SvPieChartData, StatsSVData } from '../../../../interfaces/hce/hce-stats';
import { StatsSvparam } from '../../../../shared/constants/hce/hce-stats-constants';
import { MessageModalService } from '../../../../services/message-modal/message-modal.service';
import { HceStatsService } from '../../../../services/hce/hce-stats/hce-stats.service';
import { HceSvPieChartComponent } from '../../../../shared/hce/sv-pie-chart/sv-pie-chart.component';
import { HceStatsTableComponent, TableRecords, CellItem } from '../stats-table/hce-stats-table.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';
import { SvResponse } from '../../../../interfaces/hce/hce-sv';

@Component({
  selector: 'app-hce-stats-view-sv',
  standalone: true,
  imports: [
    HceSvPieChartComponent,
    HceStatsTableComponent,
    MatGridListModule,
    NgxEchartsDirective, // use echarts [options]="opt"
    MatProgressSpinnerModule,
    NgIf,
  ],
  providers: [provideEcharts()], // Provide ECharts within this component
  templateUrl: './hce-stats-view-sv.component.html',
  styleUrl: './hce-stats-view-sv.component.css',
})
export class HceStatsViewSvComponent implements OnInit, OnChanges {
  @Input() analysis!: Analysis | null;
  @Input() sv?: SvResponse;
  @Input() refNameMap?: RefsNameMapping;
  @Output() cellFilterRequest = new EventEmitter<{
    chr?: string | number;
    svType?: string;
  }>();

  constructor(
    public dialog: MatDialog,
    private statsService: HceStatsService,
    private messageModal: MessageModalService
  ) {}

  /*
   * Pie Chart
   */

  chrOpts?: SvChrOpts = {};
  pieChartData?: SvPieChartData = {};

  /*
   * Common settings for bar charts
   */
  titleColor = 'gray';
  titleFontSize = 16;
  plotGrid = {
    left: 30, right: 20, top: 30, bottom: 70,
    containLabel: true,
    show: true,
    backgroundColor: '#FFFDF6'
  };

  /*
   * Deletions / Insertions bar chart
   */

  inDelOpt: EChartsOption = {};

  inDelTitle = 'Deletions / Insertions';
  inDelXTitle = 'SV Size (Kb)';
  inDelYTitle = '#Occurrences';
  svchartColorDel: string = "rgb(192,80,77)";
  svchartColorIns: string = "rgb(79,129,189)";

  inDelXTicks?: string[] = [];
  inDelData?: number[] = [];

  inDelColors?: string[] = [
    this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,this.svchartColorDel,
    this.svchartColorIns,this.svchartColorIns,this.svchartColorIns,this.svchartColorIns,this.svchartColorIns,this.svchartColorIns,this.svchartColorIns,this.svchartColorIns
  ];

  /*
   * Inversions bar chart
   */

  invOpt:  EChartsOption = {};

  invTitle = 'Inversions';
  invXTitle = 'SV Size (Kb)';
  invYTitle = '#Occurrences';
  svchartColorInv: string = "rgb(155,187,89)";

  invXTicks?: string[] = [];
  invData?: number[] = [];

  // Round up to a nice number
  private ceilToNiceTick(n: number): number {
    if (!isFinite(n) || n <= 0) return 1;
    const p = Math.pow(10, Math.floor(Math.log10(n)));
    return Math.ceil(n / p) * p;
  }

  /*
   * chromosome List
   */

  chromosomeDisplayedColumnLabels: string[] = [
    'CHR',
    StatsSvparam.TYPE_SV_INSERTION + 's',
    StatsSvparam.TYPE_SV_DELETION + 's',
    StatsSvparam.TYPE_SV_INVERSION + 's',
    StatsSvparam.TYPE_SV_DUPLICATION + 's',
    StatsSvparam.TYPE_SV_TRANSLOCATION + 's',
    'Total',
  ];

  chromosomeDisplayedColumns: string[] = [
    'chr',
    'ins',
    'del',
    'inv',
    'dup',
    'trans',
    'total',
  ];

  chromosomeListData?: TableRecords = [];

  isLoading = true;
  inDelReady = false;
  invReady = false;
  hasError = false;

  ngOnInit() {
  }

  ngOnChanges() {
    const hasSv =
      this.sv &&
      Object.keys(this.sv.svs ?? {}).length > 0;

    const hasRefNameMap =
      this.refNameMap &&
      Object.keys(this.refNameMap).length > 0;

    if (hasSv && hasRefNameMap) {
      this.isLoading = true;
      this.loadStatsSv();
    }
  }

  private showLoadFailed(): void {
    this.messageModal.openModal(
      $localize`Error`,
      $localize`Failed to load structural variant statistics.`
    );
  }

  private loadStatsSv() {
    if (this.sv && this.refNameMap) {
      const svJson = this.statsService.convertFromSvResponse(this.sv);
      const stats = this.statsService.buildStatsSvFromRaw(
        svJson,
        this.refNameMap,
      );

      if (stats.success) {
        this.applyStats(stats);
      } else {
        if (Array.isArray(stats.errorMessage) && stats.errorMessage.length > 0) {
          console.error(stats.errorMessage.join(', '));
        }
        this.showLoadFailed();
        this.hasError = true;
      }

      this.isLoading = false;
      return;
    }

    if (!this.analysis?.PanelID) {
      console.error('Panel ID is empty');
      this.showLoadFailed();
      this.isLoading = false;
      this.hasError = true;
      return;
    }

    // fallback
    const analysisId = this.analysis.AnalysisID;
    const panelId = this.analysis.PanelID;
    this.statsService.getStatsSv(analysisId, panelId).subscribe({
      next: (response) => {
        if (response.success) {
          this.applyStats(response);
        } else {
          if (Array.isArray(response?.errorMessage) && response.errorMessage.length > 0) {
            console.error(response.errorMessage.join(', '));
          }
          this.showLoadFailed();
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.showLoadFailed();
        this.isLoading = false;
      }
    });
  }

  private applyStats(response: StatsSVData) {
    this.chrOpts = response.chrOpts;
    this.pieChartData = response.pieChartData;

    this.svchartColorDel = response.svchart_color_del ?? '';
    this.svchartColorIns = response.svchart_color_ins ?? '';
    this.svchartColorInv = response.svchart_color_inv ?? '';

    this.inDelData = response.svchart?.InDel?.v;
    this.inDelXTicks = response.svchart?.InDel?.tick;

    this.invData = response.svchart?.Inv?.v;
    this.invXTicks = response.svchart?.Inv?.tick;

    this.chromosomeListData = [];
    for (const item of response.sv_chromosome ?? []) {
      const row: Record<string, CellItem> = {};
      for (const [k, v] of Object.entries(item)) {
        row[k] = v;
      }
      this.chromosomeListData.push(row);
    }

    this.rebuildOptions();
  }

  private rebuildOptions() {
    this.inDelOpt = {
      animation: false,
      title: { text: this.inDelTitle, left: 'center', top: 5, padding: 0, textStyle: { color: this.titleColor, fontSize: this.titleFontSize, fontWeight: 600 } },
      grid: this.plotGrid,
      xAxis: {
        type: 'category', name: this.inDelXTitle, nameLocation: 'middle', nameGap: 40,
        nameTextStyle: { align: 'center' }, data: this.inDelXTicks,
        axisTick: { alignWithLabel: true }, axisLabel: { rotate: 90 }, splitLine: { show: false },
      },
      yAxis: {
        type: 'value', name: this.inDelYTitle, nameLocation: 'middle', nameRotate: 90, nameGap: 20,
        nameTextStyle: { align: 'center', verticalAlign: 'middle' }, splitLine: { show: false }, axisLabel: { show: false },
        max: (value: any) => {
           return value.max * 1.1; // Add 10% headroom above the data
        },
      },
      series: [{
        type: 'bar',
        data: this.inDelData,
        label: { show: true, position: 'top', distance: 2, fontSize: 10 },
        itemStyle: {
          color: (p: any) => this.inDelColors ? this.inDelColors[p.dataIndex] ?? this.inDelColors[this.inDelColors.length - 1] ?? '' : ''
        }
      }]
    };

    this.invOpt = {
      animation: false,
      title: { text: this.invTitle, left: 'center', top: 5, padding: 0, textStyle: { color: this.titleColor, fontSize: this.titleFontSize, fontWeight: 600 } },
      grid: this.plotGrid,
      xAxis: {
        type: 'category', name: this.invXTitle, nameLocation: 'middle', nameGap: 40,
        nameTextStyle: { align: 'center' }, data: this.invXTicks,
        axisTick: { alignWithLabel: true }, axisLabel: { rotate: 90 }, splitLine: { show: false },
      },
      yAxis: {
        type: 'value', name: this.invYTitle, nameLocation: 'middle', nameRotate: 90, nameGap: 20,
        nameTextStyle: { align: 'center', verticalAlign: 'middle' },
        max: (v: any) => this.ceilToNiceTick(v.max * 1.0),
        splitLine: { show: false }, axisLabel: { show: false },
      },
      series: [{ type: 'bar', data: this.invData, label: { show: false }, itemStyle: { color: this.svchartColorInv } }]
    };
  }
  
  onCellFilterRequest(e: { chr?: string | number; svType?: string }) {
    this.cellFilterRequest.emit(e);
  }
  
}
